import type { ThemeRegistration } from '@ffs-studio/protocol';

export type ThemeStreamLifecycleSnapshot = {
  startupSceneName: string | null;
  mainSceneName: string | null;
  welcomeReleaseDelayMs: number;
  welcomeReleaseRule: 'leave-startup' | 'enter-main';
};

export type ThemeDiscoveryTheme = ThemeRegistration & {
  streamLifecycle: ThemeStreamLifecycleSnapshot | null;
};

export type ThemeDiscoverySnapshot = {
  themes: ThemeDiscoveryTheme[];
};

export async function loadThemeDiscovery(): Promise<ThemeDiscoverySnapshot> {
  const response = await fetch('/api/v1/effects/themes', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Theme discovery failed (${response.status}).`);
  }

  const payload = (await response.json()) as unknown;
  if (
    !payload ||
    typeof payload !== 'object' ||
    !Array.isArray((payload as { themes?: unknown }).themes)
  ) {
    throw new Error('Theme discovery returned an invalid response.');
  }

  return payload as ThemeDiscoverySnapshot;
}


export type HalloweenGhostPreset = 'off' | 'subtle' | 'strong' | 'custom';

export type HalloweenGhostCameraSnapshot = {
  settings: {
    enabled: boolean;
    preset: HalloweenGhostPreset;
    wrapperSceneName: string;
    sourceName: string | null;
    filterName: string;
    opacityPercent: number;
    saturation: number;
    brightness: number;
    contrast: number;
    verticalDriftPx: number;
    horizontalDriftPx: number;
    scaleDriftPercent: number;
    verticalPeriodMs: number;
    horizontalPeriodMs: number;
    scalePeriodMs: number;
  };
  runtime: {
    effectiveTheme: string | null;
    applied: boolean;
    lastError: string | null;
    lastActionAt: string | null;
  };
};

export async function loadHalloweenGhostCamera(): Promise<HalloweenGhostCameraSnapshot> {
  const response = await fetch('/api/v1/effects/themes/halloween/ghost-camera', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Halloween Ghost Camera settings failed (${response.status}).`);
  return response.json() as Promise<HalloweenGhostCameraSnapshot>;
}

export async function updateHalloweenGhostCamera(
  changes: Partial<HalloweenGhostCameraSnapshot['settings']>,
): Promise<HalloweenGhostCameraSnapshot> {
  const response = await fetch('/api/v1/effects/themes/halloween/ghost-camera', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  const payload = await response.json().catch(() => ({})) as { message?: string };
  if (!response.ok) throw new Error(payload.message ?? `Halloween Ghost Camera update failed (${response.status}).`);
  return payload as HalloweenGhostCameraSnapshot;
}


export async function testHalloweenWelcome(): Promise<void> {
  const response = await fetch('/api/v1/effects/themes/halloween/test-welcome', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  const result = await response.json().catch(() => ({})) as { message?: string };
  if (!response.ok) throw new Error(result.message ?? `Halloween welcome test failed (${response.status}).`);
}
